import { useRef } from "react";
import { User, Zap } from "lucide-react";
import NoteRtfEditor from "@/components/encounter/NoteRtfEditor";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ENC } from "@/constants/testIds";
import { countNoteText } from "@/lib/noteRtf";
import {
  SOAP_FIELD_GROUPS,
  SOAP_SMART_PHRASES,
  soapFieldToken,
} from "@/lib/soapFields";

export default function SoapNoteEditor({ value, onChange, readOnly, patient, placeholderContext, lockLabel }) {
  const editorRef = useRef(null);
  const counts = countNoteText(value);

  const insert = (text) => {
    if (readOnly) return;
    editorRef.current?.insertText(text);
  };

  return (
    <div
      data-testid={ENC.screenSoapEditor}
      className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
    >
      {readOnly ? (
        <div className="border-b border-slate-100 px-3 py-2">
          <p className="text-xs font-medium text-slate-500">
            {lockLabel || "Note locked after sign"}
          </p>
        </div>
      ) : null}

      <NoteRtfEditor
        ref={editorRef}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        testId={`${ENC.screenSoapEditor}-surface`}
        patient={patient}
        placeholderContext={placeholderContext}
        ariaLabel="SOAP note"
        placeholder={
          readOnly
            ? ""
            : "Start typing your SOAP note here...\nUse Insert Field or tokens such as @patientname@ to add patient and encounter data."
        }
        className="min-h-0 flex-1"
        extraToolbar={
          readOnly ? null : (
            <div className="flex flex-wrap items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-7 rounded-md px-2 text-xs"
                    data-testid={ENC.screenInsertField}
                  >
                    <User className="h-3.5 w-3.5" />
                    Insert Field
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {SOAP_FIELD_GROUPS.map((group, index) => (
                    <div key={group.id}>
                      {index > 0 ? <DropdownMenuSeparator /> : null}
                      <DropdownMenuLabel className="text-xs uppercase tracking-wide text-slate-400">
                        {group.label}
                      </DropdownMenuLabel>
                      {group.fields.map((field) => (
                        <DropdownMenuItem
                          key={field.token}
                          onClick={() => insert(soapFieldToken(field.token))}
                        >
                          {field.label}
                          <span className="ml-auto font-fhir text-[10px] text-slate-400">
                            {soapFieldToken(field.token)}
                          </span>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-7 rounded-md px-2 text-xs"
                    data-testid={ENC.screenInsertSmartPhrase}
                  >
                    <Zap className="h-3.5 w-3.5" />
                    Insert SmartPhrase
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="text-xs uppercase tracking-wide text-slate-400">
                    Starter templates
                  </DropdownMenuLabel>
                  {SOAP_SMART_PHRASES.map((phrase) => (
                    <DropdownMenuItem key={phrase.id} onClick={() => insert(phrase.body)}>
                      {phrase.label}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled>SmartPhrase library coming later</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        }
      />

      <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2 text-xs text-slate-400">
        <span>
          Words: {counts.words} &nbsp;|&nbsp; Characters: {counts.characters}
        </span>
        {readOnly ? <span className="font-medium text-slate-500">{lockLabel || "Note locked after sign"}</span> : null}
      </div>
    </div>
  );
}
